/**
 * Rashi data + web-layer helpers.
 *
 * Raw canonical data comes from @ishvaram/domain-data (generated from
 * backend YAML). rashi-presentation.ts owns the lightweight display projection;
 * this file adds the compatibility engine:
 * - RASHIS / RASHI_MAP — web-shaped array and lookup
 * - RASHI_EMOJIS, ELEMENT_COLORS — visual helpers
 * - LIFE_PROBLEMS, HOUSE_MEANINGS, PLANET_INFO — page content
 * - Compatibility scoring functions
 *
 * Static lookup tables live in rashi-compatibility-tables.ts.
 * Consumers: import from "@/lib/rashi"
 */

import {
  VARNA,
  VARNA_RANK,
  VASHYA,
  VASHYA_MATRIX,
  P_FRIENDS,
  P_ENEMIES,
  BHAKOOT_BAD,
  ELEM_COMPAT,
  DIM_EMOTIONAL,
  DIM_PHYSICAL,
  DIM_COMMUNICATION,
  DIM_FAMILY,
} from "./rashi-compatibility-tables";
import { PLANET_INFO } from "./rashi-content-data";
import { type Rashi } from "./rashi-presentation";

export { RASHIS, RASHI_MAP, RASHI_EMOJIS } from "./rashi-presentation";
export type { Rashi } from "./rashi-presentation";

// ─── Vedic Compatibility Engine (Ashtakoot — rashi-computable kootas) ────────
//
// 4 of the 8 Gun Milan kootas can be computed from rashi alone without birth data:
//   Varna (1), Vashya (2), Graha Maitri (5), Bhakoot (7) = max 15 pts
// The remaining 4 (Tara, Yoni, Gana, Nadi = 21 pts) require exact nakshatra.
// Data mirrors backend data/matching.yaml and data/planets.yaml exactly.

function _pRel(p1: string, p2: string): "friend" | "neutral" | "enemy" {
  if (P_FRIENDS[p1]?.includes(p2)) return "friend";
  if (P_ENEMIES[p1]?.includes(p2)) return "enemy";
  return "neutral";
}

function _varna(a: Rashi, b: Rashi): number {
  const ra = VARNA_RANK[VARNA[a.id]] ?? 1;
  const rb = VARNA_RANK[VARNA[b.id]] ?? 1;
  return ra >= rb ? 1 : 0;
}

function _vashya(a: Rashi, b: Rashi): number {
  return VASHYA_MATRIX[VASHYA[a.id] ?? "manav"]?.[VASHYA[b.id] ?? "manav"] ?? 0;
}

function _grahaMaitri(a: Rashi, b: Rashi): number {
  if (a.ruler === b.ruler) return 5;
  const ab = _pRel(a.ruler, b.ruler);
  const ba = _pRel(b.ruler, a.ruler);
  const pair = [ab, ba].sort().join("-");
  if (pair === "friend-friend") return 5;
  if (pair === "friend-neutral") return 4;
  if (pair === "neutral-neutral") return 3;
  if (pair === "enemy-friend") return 1;
  if (pair === "enemy-neutral") return 0.5;
  return 0;
}

function _bhakoot(a: Rashi, b: Rashi): number {
  const diff = Math.abs(a.number - b.number);
  const minDiff = diff > 6 ? 12 - diff : diff;
  if (minDiff === 0) return 7;
  return BHAKOOT_BAD.has(minDiff) ? 0 : 7;
}

export interface KootaResult {
  name: string;
  name_hi: string;
  score: number;
  max: number;
  description: string;
}

export interface KootaBreakdown {
  kootas: KootaResult[];
  total: number;
  max: 15;
  percentage: number;
}

export function getKootaBreakdown(a: Rashi, b: Rashi): KootaBreakdown {
  const bhakoot = _bhakoot(a, b);
  const gm = _grahaMaitri(a, b);
  const vashya = _vashya(a, b);
  const varna = _varna(a, b);

  const kootas: KootaResult[] = [
    {
      name: "Bhakoot",
      name_hi: "भकूट",
      score: bhakoot,
      max: 7,
      description: bhakootDescription(a, b, bhakoot),
    },
    {
      name: "Graha Maitri",
      name_hi: "ग्रह मैत्री",
      score: gm,
      max: 5,
      description: grahaMaitriDescription(a, b, gm),
    },
    {
      name: "Vashya",
      name_hi: "वश्य",
      score: vashya,
      max: 2,
      description: vashyaDescription(vashya),
    },
    {
      name: "Varna",
      name_hi: "वर्ण",
      score: varna,
      max: 1,
      description: varnaDescription(a, b, varna),
    },
  ];

  const total = kootas.reduce((s, k) => s + k.score, 0);
  return { kootas, total, max: 15, percentage: Math.round((total / 15) * 100) };
}

function rashiDistance(a: Rashi, b: Rashi): number {
  const diff = Math.abs(a.number - b.number);
  return diff > 6 ? 12 - diff : diff;
}

function bhakootDescription(a: Rashi, b: Rashi, bhakoot: number): string {
  if (bhakoot === 7) {
    return "Favorable rashi positions — harmonious emotional and financial compatibility.";
  }
  const distance = rashiDistance(a, b);
  return `Bhakoot Dosha present — ${distance + 1}/${13 - distance} rashi positions. Traditional remedies can mitigate this.`;
}

function grahaMaitriDescription(a: Rashi, b: Rashi, gm: number): string {
  const rA = PLANET_INFO[a.ruler]?.name_en ?? a.ruler;
  const rB = PLANET_INFO[b.ruler]?.name_en ?? b.ruler;
  if (a.ruler === b.ruler) return `Same ruling planet (${rA}) — natural mental harmony.`;
  const ab = _pRel(a.ruler, b.ruler);
  const ba = _pRel(b.ruler, a.ruler);
  return `${rA} (${ab}) and ${rB} (${ba}) — ${grahaMaitriStrength(gm)} mental compatibility.`;
}

function grahaMaitriStrength(gm: number): string {
  if (gm >= 4) return "strong";
  if (gm >= 2) return "moderate";
  return "weak";
}

function vashyaDescription(vashya: number): string {
  if (vashya === 2) {
    return "Compatible Vashya groups — natural mutual influence and cooperation.";
  }
  if (vashya === 1) return "Partial Vashya compatibility — one-way influence.";
  return "Different Vashya groups — requires conscious effort to align.";
}

function varnaDescription(a: Rashi, b: Rashi, varna: number): string {
  const pairLabel = `${VARNA[a.id] ?? "—"} and ${VARNA[b.id] ?? "—"}`;
  if (varna === 1) return `${pairLabel} — spiritual compatibility present.`;
  return `${pairLabel} — spiritual paths diverge slightly.`;
}

// ─── Element-pair key helper ──────────────────────────────────────────────────

type Element = "fire" | "earth" | "air" | "water";

function _elemKey(a: Element, b: Element): string {
  return [a, b].sort().join("-");
}

export interface DimensionScores {
  emotional: number;
  physical: number;
  communication: number;
  family: number;
}

export function getDimensionScores(a: Rashi, b: Rashi): DimensionScores {
  const k = _elemKey(a.element, b.element);
  return {
    emotional: DIM_EMOTIONAL[k] ?? 60,
    physical: DIM_PHYSICAL[k] ?? 60,
    communication: DIM_COMMUNICATION[k] ?? 60,
    family: DIM_FAMILY[k] ?? 60,
  };
}

export function getCompatibilityScore(a: Rashi, b: Rashi): number {
  const ek = _elemKey(a.element, b.element);
  const elemPct = ((ELEM_COMPAT[ek] ?? 5) / 9) * 100;
  const koota = getKootaBreakdown(a, b);
  const kootaMap = Object.fromEntries(koota.kootas.map((k) => [k.name, k]));
  const gmPct = ((kootaMap["Graha Maitri"]?.score ?? 0) / 5) * 100;
  const bhakootPct = bhakootPercentage(kootaMap["Bhakoot"]?.score ?? 0);
  const vashyaPct = ((kootaMap["Vashya"]?.score ?? 0) / 2) * 100;
  const varnaPct = (kootaMap["Varna"]?.score ?? 0) * 100;
  const blended =
    0.35 * elemPct + 0.3 * gmPct + 0.2 * bhakootPct + 0.1 * vashyaPct + 0.05 * varnaPct;
  return Math.round(Math.min(100, Math.max(10, blended)));
}

function bhakootPercentage(score: number): number {
  return score === 7 ? 100 : 25;
}

export function getCompatibilityLevel(score: number): string {
  if (score >= 80) return "Excellent";
  if (score >= 65) return "Good";
  if (score >= 45) return "Moderate";
  if (score >= 30) return "Challenging";
  return "Difficult";
}

/** Hindi words for getCompatibilityLevel()'s levels, for Hindi pages. */
export const COMPATIBILITY_LEVEL_HI: Readonly<Record<string, string>> = {
  Excellent: "उत्कृष्ट",
  Good: "अच्छा",
  Moderate: "मध्यम",
  Challenging: "चुनौतीपूर्ण",
  Difficult: "कठिन",
};

export function getCompatibilityColor(score: number): string {
  if (score >= 80) return "var(--color-success)";
  if (score >= 65) return "var(--color-info)";
  if (score >= 45) return "var(--color-warning-gold)";
  if (score >= 30) return "var(--color-warning-orange)";
  return "var(--color-error)";
}
